/**
 * Auth Store
 *
 * Zustand store for authentication state management.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, AuthState } from '@/types';
import { wsService } from '@/services/websocket';

interface AuthStore extends AuthState {
  login: (user: User, accessToken: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (user: Partial<User>) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: (user, accessToken) => {
        set({
          user,
          accessToken,
          isAuthenticated: true,
          isLoading: false,
        });
        // Connect WebSocket after auth
        wsService.connect();
      },

      logout: () => {
        wsService.disconnect();
        // Clear all persisted stores to prevent data leaking across accounts
        localStorage.removeItem('aerware-onboarding');
        localStorage.removeItem('aerware-subscription');
        localStorage.removeItem('aerware-iris-chat');
        localStorage.removeItem('aerware-refresh-token');
        // Clear onboarding conversation messages (CRITICAL: prevents data leak between accounts)
        localStorage.removeItem('onboarding_path_a_messages');
        localStorage.removeItem('onboarding_path_b_messages');
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: (isLoading) => set({ isLoading }),

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),
    }),
    {
      name: 'aerware-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        user: state.user,
      }),
      // Derive isAuthenticated from rehydrated accessToken
      onRehydrateStorage: () => (state) => {
        if (state?.accessToken && state?.user) {
          state.isAuthenticated = true;
          // Reconnect WebSocket if we have a valid session
          wsService.connect();
        }
      },
    }
  )
);

/**
 * Get access token for API requests
 */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

/**
 * Check if user has specified role
 */
export function hasRole(requiredRole: User['role']): boolean {
  const user = useAuthStore.getState().user;
  if (!user) return false;
  return user.role === requiredRole;
}

/**
 * Check if user is an Admin (full access)
 */
export function isAdmin(): boolean {
  const user = useAuthStore.getState().user;
  return user?.role === 'admin';
}
